"use client";

import {
  createContext,
  useCallback,
  use,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import type {
  DiscoveryProduct,
  DiscoveryMetadata,
} from "@/components/discovery/types";

// WHY: content-complete equality (JSON.stringify, not a field list — the map also renders `dimensions`, an object) so `setProducts` can bail on unchanged data and break the REPACKIFY-1HV render loop; a partial compare would either reform the loop or leave stale map markers.
export function productListsEqual(
  left: DiscoveryProduct[],
  right: DiscoveryProduct[],
): boolean {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  return JSON.stringify(left) === JSON.stringify(right);
}

interface ResultsContextValue {
  products: DiscoveryProduct[];
  setProducts: (products: DiscoveryProduct[]) => void;
  metadata: DiscoveryMetadata | null;
  setMetadata: (metadata: DiscoveryMetadata | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

const ResultsContext = createContext<ResultsContextValue | null>(null);

export function ResultsProvider({ children }: { children: ReactNode }) {
  const [products, setProductsState] = useState<DiscoveryProduct[]>([]);
  const [metadata, setMetadata] = useState<DiscoveryMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setProducts = useCallback((next: DiscoveryProduct[]) => {
    setProductsState((prev) => (productListsEqual(prev, next) ? prev : next));
  }, []);

  const value = useMemo(
    () => ({
      products,
      setProducts,
      metadata,
      setMetadata,
      isLoading,
      setIsLoading,
      error,
      setError,
    }),
    [products, setProducts, metadata, isLoading, error],
  );

  return (
    <ResultsContext value={value}>{children}</ResultsContext>
  );
}

const defaultResultsContext: ResultsContextValue = {
  products: [],
  setProducts: () => {},
  metadata: null,
  setMetadata: () => {},
  isLoading: false,
  setIsLoading: () => {},
  error: null,
  setError: () => {},
};

export function useResultsContext() {
  const context = use(ResultsContext);
  return context ?? defaultResultsContext;
}

export { ResultsContext };
